import { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { getHighLevelContext } from '@/utils/highlevel';
import toast from 'react-hot-toast';
import type { SyncEntityType, SyncDirection, SyncHistoryEntry } from '@/types';

/**
 * Track active syncs by entity type
 */
interface ActiveSyncs {
  contacts: string | null;
  products: string | null;
  invoices: string | null;
}

export function useManualSync() {
  const queryClient = useQueryClient();
  const context = getHighLevelContext();
  const locationId = context?.locationId || '';
  const [jobIds, setJobIds] = useState<string[]>([]);
  const [activeSyncs, setActiveSyncs] = useState<ActiveSyncs>({
    contacts: null,
    products: null,
    invoices: null,
  });

  const triggerSyncMutation = useMutation({
    mutationFn: async ({
      entityType,
      direction,
      fullSync = false,
    }: {
      entityType: SyncEntityType;
      direction: SyncDirection;
      fullSync?: boolean;
    }) => {
      return apiClient.triggerManualSync(locationId, entityType, direction, fullSync);
    },
    onSuccess: (data, variables) => {
      setJobIds(data.jobIds);

      // Track the sync history ID for this entity type
      if (data.syncHistoryId) {
        setActiveSyncs(prev => ({
          ...prev,
          [variables.entityType]: data.syncHistoryId,
        }));
      }

      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['sync-status', locationId] });
      queryClient.invalidateQueries({ queryKey: ['sync-jobs', locationId] });
      queryClient.invalidateQueries({ queryKey: ['sync-stats', locationId] });

      const entityLabel = variables.entityType.charAt(0).toUpperCase() + variables.entityType.slice(1);
      const directionLabel =
        variables.direction === 'to_xero'
          ? 'to Xero'
          : variables.direction === 'from_xero'
          ? 'from Xero'
          : 'bidirectional';
      const syncTypeLabel = variables.fullSync ? 'Full' : 'Smart';

      toast.success(`${syncTypeLabel} ${entityLabel.toLowerCase()} sync ${directionLabel} started`, {
        icon: '🔄',
        duration: 4000,
      });
    },
    onError: (error: Error, variables) => {
      const entityLabel = variables.entityType.charAt(0).toUpperCase() + variables.entityType.slice(1);
      toast.error(`Failed to start ${entityLabel} sync: ${error.message}`, {
        duration: 5000,
      });
    },
  });

  const triggerSync = async (
    entityType: SyncEntityType,
    direction: SyncDirection,
    fullSync: boolean = false
  ) => {
    return triggerSyncMutation.mutateAsync({ entityType, direction, fullSync });
  };

  /**
   * Callback to clear the active sync for an entity type when it completes
   */
  const clearActiveSync = useCallback((entityType: SyncEntityType) => {
    setActiveSyncs(prev => ({
      ...prev,
      [entityType]: null,
    }));
    // Refresh data after sync completes
    queryClient.invalidateQueries({ queryKey: ['sync-status', locationId] });
    queryClient.invalidateQueries({ queryKey: ['sync-stats', locationId] });
    queryClient.invalidateQueries({ queryKey: ['sync-history', locationId] });
  }, [queryClient, locationId]);

  /**
   * Callback when a sync completes - used by SyncProgressIndicator
   */
  const handleSyncComplete = useCallback((entityType: SyncEntityType, entry: SyncHistoryEntry) => {
    clearActiveSync(entityType);

    // Show completion toast based on status
    if (entry.status === 'completed') {
      const stats = [];
      if (entry.createdRecords > 0) stats.push(`${entry.createdRecords} created`);
      if (entry.updatedRecords > 0) stats.push(`${entry.updatedRecords} updated`);
      const summary = stats.length > 0 ? stats.join(', ') : `${entry.successfulRecords} synced`;

      toast.success(`Sync complete: ${summary}`, {
        icon: '✅',
        duration: 5000,
      });
    } else if (entry.status === 'partial_success') {
      toast(`Sync partially complete: ${entry.successfulRecords} synced, ${entry.failedRecords} failed`, {
        icon: '⚠️',
        duration: 5000,
      });
    } else if (entry.status === 'failed') {
      toast.error(entry.errorSummary || `Sync failed with ${entry.failedRecords} errors`, {
        duration: 6000,
      });
    }
  }, [clearActiveSync]);

  return {
    triggerSync,
    isLoading: triggerSyncMutation.isPending,
    error: triggerSyncMutation.error,
    jobIds,
    activeSyncs,
    handleSyncComplete,
  };
}
